import { useEffect, useMemo, useState } from "react";
import client from "../api/client";

const emptyForm = {
  customerCode: "",
  customerName: "",
  email: "",
  phone: "",
  addressLine: "",
  gstNo: "",
};

const styles = {
  page: {
    fontSize: 11,
    height: "calc(100vh - 20px)",
    overflowY: "auto",
    padding: 0,
  },
  card: {
    border: 0,
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 2px 10px rgba(0,0,0,.08)",
    background: "#fff",
  },
  header: {
    background: "linear-gradient(90deg,#0b4f4a,#0f766e)",
    color: "#fff",
    padding: "8px 12px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  title: { fontSize: 13, fontWeight: 700, margin: 0 },
  sub: { fontSize: 10, opacity: 0.85 },
  body: { padding: 10 },
  input: { height: 30, fontSize: 11, borderRadius: 7 },
  btn: {
    height: 28,
    fontSize: 11,
    borderRadius: 7,
    fontWeight: 600,
    padding: "3px 9px",
  },
  tableWrap: {
    maxHeight: 330,
    overflowY: "auto",
    overflowX: "auto",
    border: "1px solid #e9ecef",
    borderRadius: 10,
  },
  table: { fontSize: 11, marginBottom: 0 },
  th: {
    fontSize: 10,
    padding: "7px 8px",
    whiteSpace: "nowrap",
    color: "#fff",
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  td: {
    fontSize: 11,
    padding: "6px 8px",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
};

export default function CustomersPage() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    load();
  }, []);

  const getValue = (row, p, c) => row?.[p] ?? row?.[c] ?? "";

  const load = async () => {
    try {
      setLoading(true);
      const res = await client.get("/sales/customers");
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch {
      alert("Failed to load customers");
    } finally {
      setLoading(false);
    }
  };

  const filteredRows = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return rows;

    return rows.filter((r) =>
      `${getValue(r, "CustomerCode", "customerCode")}
       ${getValue(r, "CustomerName", "customerName")}
       ${getValue(r, "Phone", "phone")}`
        .toLowerCase()
        .includes(q),
    );
  }, [rows, search]);

  const save = async () => {
    if (!form.customerName) {
      return alert(
        "Customer Name required. Code will be auto-generated if blank.",
      );
    }

    try {
      setSaving(true);

      const payload = {
        ...form,
        companyId: Number(localStorage.getItem("companyId") || 0),
      };
      if (editingId) await client.put(`/sales/customers/${editingId}`, payload);
      else await client.post("/sales/customers", payload);

      setForm(emptyForm);
      setEditingId(null);
      load();
    } catch {
      alert("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const editRow = (r) => {
    setEditingId(r.Id ?? r.id);
    setForm({
      customerCode: getValue(r, "CustomerCode", "customerCode"),
      customerName: getValue(r, "CustomerName", "customerName"),
      email: getValue(r, "Email", "email"),
      phone: getValue(r, "Phone", "phone"),
      addressLine: getValue(r, "AddressLine", "addressLine"),
      gstNo: getValue(r, "GSTNo", "gstNo"),
    });
  };

  const deleteRow = async (r) => {
    if (!window.confirm("Delete customer?")) return;
    await client.delete(`/sales/customers/${r.Id ?? r.id}`);
    load();
  };

  const clear = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  return (
    <div className="container-fluid py-2" style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div>
            <h6 style={styles.title}>
              <i className="bi bi-person-vcard me-2"></i>
              Customers
            </h6>
            <div style={styles.sub}>
              Manage customer master, GST and contact details
            </div>
          </div>

          {editingId && (
            <span className="badge text-bg-warning" style={{ fontSize: 10 }}>
              Edit Mode
            </span>
          )}
        </div>

        <div style={styles.body}>
          <div className="row g-2 mb-2">
            <div className="col-md-2">
              <input
                placeholder="Auto Code"
                className="form-control form-control-sm"
                style={styles.input}
                value={form.customerCode}
                onChange={(e) =>
                  setForm({ ...form, customerCode: e.target.value })
                }
              />
            </div>

            <div className="col-md-3">
              <input
                placeholder="Customer Name *"
                className="form-control form-control-sm"
                style={styles.input}
                value={form.customerName}
                onChange={(e) =>
                  setForm({ ...form, customerName: e.target.value })
                }
              />
            </div>

            <div className="col-md-2">
              <input
                placeholder="Phone"
                className="form-control form-control-sm"
                style={styles.input}
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>

            <div className="col-md-2">
              <input
                placeholder="GST No"
                className="form-control form-control-sm"
                style={styles.input}
                value={form.gstNo}
                onChange={(e) => setForm({ ...form, gstNo: e.target.value })}
              />
            </div>

            <div className="col-md-3">
              <input
                placeholder="Email"
                className="form-control form-control-sm"
                style={styles.input}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>

            <div className="col-md-12">
              <input
                placeholder="Address"
                className="form-control form-control-sm"
                style={styles.input}
                value={form.addressLine}
                onChange={(e) =>
                  setForm({ ...form, addressLine: e.target.value })
                }
              />
            </div>

            <div className="col-md-12 d-flex gap-2">
              <button
                className={`btn btn-sm ${editingId ? "btn-warning" : "btn-success"}`}
                style={styles.btn}
                onClick={save}
                disabled={saving}
              >
                <i className="bi bi-check2-circle me-1"></i>
                {saving ? "Saving..." : editingId ? "Update" : "Save"}
              </button>

              <button
                className="btn btn-sm btn-outline-secondary"
                style={styles.btn}
                onClick={clear}
              >
                <i className="bi bi-x-circle me-1"></i>
                Clear
              </button>

              <button
                className="btn btn-sm btn-outline-primary"
                style={styles.btn}
                onClick={load}
              >
                <i className="bi bi-arrow-clockwise me-1"></i>
                Refresh
              </button>
            </div>
          </div>

          <div className="d-flex justify-content-between align-items-center mb-2">
            <input
              className="form-control form-control-sm"
              style={{ ...styles.input, maxWidth: 280 }}
              placeholder="Search customer..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <span style={{ fontSize: 10, color: "#6c757d" }}>
              Total: <strong>{filteredRows.length}</strong>
            </span>
          </div>

          <div style={styles.tableWrap}>
            <table
              className="table table-bordered table-hover align-middle"
              style={styles.table}
            >
              <thead>
                <tr>
                  <th style={styles.th}>Code</th>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Phone</th>
                  <th style={styles.th}>GST</th>
                  <th style={{ ...styles.th, width: 95 }}>Action</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="text-center text-muted"
                      style={{ ...styles.td, padding: 18 }}
                    >
                      Loading customers...
                    </td>
                  </tr>
                ) : filteredRows.length ? (
                  filteredRows.map((r, i) => (
                    <tr key={r.Id ?? r.id ?? i}>
                      <td style={{ ...styles.td, fontWeight: 700 }}>
                        {getValue(r, "CustomerCode", "customerCode")}
                      </td>
                      <td style={styles.td}>
                        {getValue(r, "CustomerName", "customerName")}
                      </td>
                      <td style={styles.td}>{getValue(r, "Phone", "phone")}</td>
                      <td style={styles.td}>
                        {getValue(r, "GSTNo", "gstNo") || "-"}
                      </td>
                      <td style={styles.td}>
                        <button
                          className="btn btn-sm btn-outline-primary me-1"
                          style={{
                            ...styles.btn,
                            height: 25,
                            padding: "2px 7px",
                          }}
                          onClick={() => editRow(r)}
                        >
                          <i className="bi bi-pencil-square"></i>
                        </button>

                        <button
                          className="btn btn-sm btn-outline-danger"
                          style={{
                            ...styles.btn,
                            height: 25,
                            padding: "2px 7px",
                          }}
                          onClick={() => deleteRow(r)}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="5"
                      className="text-center text-muted"
                      style={{ ...styles.td, padding: 18 }}
                    >
                      No customers found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
